// Core
import React from 'react';

// Libraries
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import { timestampToDateStrText, timestampToTimeAgo } from '../../../../../../plugins/general';
import {
  formatCrusherModel,
  runningStatusToEquipmentStatus,
  runningStatusToText,
} from '../../../../../../plugins/crusher';

// Types
import { Theme } from '../../../../../../types/theme';
import { CrusherModel } from '../../../../../../types/crusher';
import {
  CrusherDrawerType,
  EdgeDeviceOperationalStatusStatus,
} from '../../../../../../types/global';

// Media
import VSIImage from '../../../../../../media/images/equipment/VSI.avif';

// Components
import {
  ConnectionStatus,
  ConnectionStatusStatus,
} from '../../../../../../components/ConnectionStatus';
import { EquipmentStatusTag } from '../../../../../../components/EquipmentStatusTag';
import {
  Button,
  ButtonSize,
  ButtonType,
} from '../../../../../../layout/elements/Button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbType,
} from '../../../../../../layout/elements/Breadcrumb';
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import {
  Title,
  TitleSize,
  TitleWeight,
} from '../../../../../../layout/elements/Title';
import { Tooltip } from '../../../../../../layout/elements/Tooltip';
import {
  Alarm,
  CircleInfo,
  Information,
  LineChart,
  ListCheck,
  MapOutline,
  Settings,
  Tools,
} from '../../../../../../layout/icons';
import { ViewType } from '../../../../../../components/CameraFeed';

// View
import {
  // AlarmButton,
  ButtonContainer,
  CloudConnectionContainer,
  CloudConnectionContent,
  ContentContainer,
  CrusherInfoContainer,
  CrusherStatus,
  DetailsContainer,
  EquipmentImageContainer,
  EquipmentImageContainerBelow,
  EquipmentName,
  EquipmentStatusContainer,
  LastCloudConnection,
  LastUpdateTime,
  OverviewContainer,
  OverviewContent,
  OverviewWrapper,
  RegisteredMark,
  TooltipIconContainer,
} from './Details.style';
import { DetailsProps } from './Details.types';

function Details({
  className,
  edgeStatus,
  equipmentUnitCrusher,
  onDrawerOpen,
  onModalOpen,
  testId,
  unitData,
}: DetailsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  const {
    isXs,
    isSm,
    isMd,
    isLg,
  } = useBreakpoint();
  const isMobileOrTablet: boolean = isXs || isSm || isMd || isLg;
  const isDesktop: boolean = !isMobileOrTablet;

  const hasHiddenModules: boolean = !!unitData
    && (!unitData.metalDetectorEnable || !unitData.feedSuperiorEnable);

  // Function to format the equipment title
  const formattedTitle = (): JSX.Element => {
    const formatModelWithJSX = (model: CrusherModel): JSX.Element => {
      const formatted: string = formatCrusherModel(model);

      if (formatted.includes('®')) {
        const parts: string[] = formatted.split('®');
        return (
          <>
            {parts[0]}
            <RegisteredMark>
              ®
            </RegisteredMark>
            {parts[1]}
          </>
        );
      }

      return <span>{formatted}</span>;
    };

    return (
      isXs ? (
        <Paragraph
          size={ParagraphSize.lg}
          weight={ParagraphWeight.bold}
          color={theme.color.emptyBox.titleColor}
        >
          {
            equipmentUnitCrusher.customerUnitName
            || (formatModelWithJSX(equipmentUnitCrusher.model))
          }
        </Paragraph>
      ) : (
        <Title size={TitleSize.xxs} weight={TitleWeight.bold} className="title">
          {
            equipmentUnitCrusher.customerUnitName
            || (formatModelWithJSX(equipmentUnitCrusher.model))
          }
        </Title>
      )
    );
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { text: t('common.plant'), target: '/plant-management' },
    {
      text: equipmentUnitCrusher.customerUnitName || formatCrusherModel(equipmentUnitCrusher.model),
    },
  ];

  return (
    <DetailsContainer
      className={className}
      data-testid={testId}
    >
      <ContentContainer>
        <Row>
          <Breadcrumb
            type={BreadcrumbType.plant}
            items={breadcrumbItems}
            testId="breadcrumb"
          />
        </Row>
        <CrusherStatus>
          <EquipmentStatusContainer>
            <EquipmentStatusTag
              status={runningStatusToEquipmentStatus(
                unitData?.unitStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
              text={runningStatusToText(unitData?.unitStatus)}
              testId="equipment-status"
            />
            {isMobileOrTablet && (
              <Tooltip text={t('common.alarm.plural')}>
                <Button
                  size={ButtonSize.xs}
                  square
                  disabled
                  className="button-actions"
                  type={ButtonType.secondary}
                  testId="alarm-button-mobile"
                  onClick={() => {}}
                >
                  <Alarm />
                </Button>
              </Tooltip>
            )}
          </EquipmentStatusContainer>
          <EquipmentName>
            {formattedTitle()}
          </EquipmentName>

          <Paragraph
            size={ParagraphSize.sm}
            color={theme.color.crusherDashboard.detailsModule.lastUpdateTextColor}
            testId="last-update-item"
          >
            <Trans
              i18nKey="common.lastUpdate"
              values={{ lastUpdate: timestampToTimeAgo(unitData?.timestamp) }}
              components={{ lastUpdateTime: <LastUpdateTime /> }}
            />
          </Paragraph>

        </CrusherStatus>

        {hasHiddenModules && isDesktop && (
          <EquipmentImageContainerBelow>
            <img
              src={VSIImage}
              alt={formatCrusherModel(equipmentUnitCrusher.model)}
              data-testid="equipment-model-image-below"
            />
          </EquipmentImageContainerBelow>
        )}

        <OverviewWrapper>
          <OverviewContainer>
            <OverviewContent>
              <CrusherInfoContainer>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  className="overview-section"
                >
                  {t('common.overview')}
                </Paragraph>
              </CrusherInfoContainer>

              <CloudConnectionContainer>
                <CloudConnectionContent>
                  <Paragraph
                    size={ParagraphSize.sm}
                    color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                    className="paragraph-sm"
                  >
                    {t('common.cloudConnection')}
                  </Paragraph>

                  <Tooltip
                    text={(
                      <Trans
                        i18nKey="common.lastUpdate"
                        values={{ lastUpdate: timestampToTimeAgo(edgeStatus?.timestamp) }}
                        components={{ lastUpdateTime: <LastCloudConnection /> }}
                      />
                    )}
                  >
                    <TooltipIconContainer>
                      <CircleInfo />
                    </TooltipIconContainer>
                  </Tooltip>
                </CloudConnectionContent>

                <ConnectionStatus
                  status={
                    edgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
                      ? ConnectionStatusStatus.online
                      : ConnectionStatusStatus.offline
                  }
                  testId="connection-status"
                  className="connection-status"
                />
              </CloudConnectionContainer>

              <CrusherInfoContainer>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                  className="paragraph-sm"
                >
                  {t('common.superiorPartNumber')}
                </Paragraph>
                <Paragraph
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                  align={ParagraphAlign.right}
                  color={theme.color.crusherDashboard.detailsModule.valuesTextColor}
                  className="paragraph-sm"
                >
                  {formatCrusherModel(equipmentUnitCrusher.model)}
                </Paragraph>
              </CrusherInfoContainer>

              <CrusherInfoContainer>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                  className="paragraph-sm"
                >
                  {t('common.serialNumber')}
                </Paragraph>
                <Paragraph
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                  align={ParagraphAlign.right}
                  color={theme.color.crusherDashboard.detailsModule.valuesTextColor}
                  className="paragraph-sm"
                >
                  {equipmentUnitCrusher.serialNumber}
                </Paragraph>
              </CrusherInfoContainer>

              <CrusherInfoContainer>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.detailsModule.parametersTextColor}
                  className="paragraph-sm"
                >
                  {t('common.installedAt')}
                </Paragraph>
                <Paragraph
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                  align={ParagraphAlign.right}
                  color={theme.color.crusherDashboard.detailsModule.valuesTextColor}
                  className="paragraph-sm"
                >
                  {timestampToDateStrText(equipmentUnitCrusher.installedAt)}
                </Paragraph>
              </CrusherInfoContainer>
            </OverviewContent>
          </OverviewContainer>

          {(isMd || isLg) && (
            <EquipmentImageContainer>
              <img
                src={VSIImage}
                alt={formatCrusherModel(equipmentUnitCrusher.model)}
                data-testid="equipment-model-image"
              />
            </EquipmentImageContainer>
          )}
        </OverviewWrapper>

        {isMobileOrTablet ? (
          <ButtonContainer>
            <Button
              size={ButtonSize.sm}
              type={ButtonType.secondary}
              testId="trends-button-mobile"
              onClick={(): void => onDrawerOpen(CrusherDrawerType.trending)}
            >
              <LineChart />
              {t('common.trends')}
            </Button>
            <Button
              size={ButtonSize.sm}
              type={ButtonType.secondary}
              testId="view-camera-feed-button"
              onClick={(): void => onModalOpen(ViewType.map)}
            >
              <MapOutline />
              {t('common.viewMap')}
            </Button>
          </ButtonContainer>
        ) : (
          <ButtonContainer>
            <Tooltip className="tooltip" text={t('common.settings')}>
              <Button
                square
                className="button-actions"
                size={ButtonSize.xs}
                disabled
                type={ButtonType.secondary}
                testId="settings-button"
                onClick={() => {}}
              >
                <Settings />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.trending')}>
              <Button
                square
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="trending-button"
                onClick={(): void => onDrawerOpen(CrusherDrawerType.trending)}
              >
                <LineChart />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.maintenance')}>
              <Button
                square
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="maintenance-button"
                onClick={() => onDrawerOpen(CrusherDrawerType.maintenance)}
              >
                <Tools />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.timeline')}>
              <Button
                square
                disabled
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="timeline-button"
                onClick={() => {}}
              >
                <ListCheck />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.info')}>
              <Button
                square
                disabled
                className="button-actions"
                size={ButtonSize.xs}
                type={ButtonType.secondary}
                testId="info-button"
                onClick={() => {}}
              >
                <Information />
              </Button>
            </Tooltip>
            <Tooltip className="tooltip" text={t('common.alarm.plural')}>
              <Button
                size={ButtonSize.xs}
                square
                disabled
                className="button-actions"
                type={ButtonType.secondary}
                testId="alarm-button"
                onClick={() => {}}
              >
                <Alarm />
              </Button>
            </Tooltip>
          </ButtonContainer>
        )}
      </ContentContainer>
    </DetailsContainer>
  );
}

export { Details };
